import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, MapPin, ImageIcon, Sparkles } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PhotoManager } from "@/components/admin/PhotoManager";
import { SeasonPricesEditor, cleanSeasons, withKeys } from "@/components/admin/SeasonPricesEditor";
import { mediaUrl } from "@/lib/pricing";
import { Panel, BRL, inputCls, labelCls, btnPrimary, btnGhost, iconBtn } from "./ui";

const EMPTY = {
  title: "", city: "", state: "RJ", description: "", price_per_night: "", deposit_percent: 50,
  images: [], max_guests: 4, bedrooms: 2, bathrooms: 1, amenities: "", highlight: "", season_prices: [],
};

const Num = ({ label, value, onChange, testId }) => (
  <div>
    <label className={labelCls}>{label}</label>
    <input type="number" value={value} onChange={(e) => onChange(e.target.value)} className={inputCls} data-testid={testId} />
  </div>
);

const PropertyForm = ({ open, onOpenChange, initial, onSave }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const set = (patch) => setF((x) => ({ ...x, ...patch }));

  useEffect(() => {
    setF(initial ? { ...EMPTY, ...initial, amenities: (initial.amenities || []).join(", "), highlight: initial.highlight || "",
      images: initial.images || [], season_prices: withKeys(initial.season_prices || []) } : EMPTY);
  }, [initial, open]);

  const submit = async () => {
    if (!f.title || !f.city || !f.price_per_night) return toast.error("Preencha nome, cidade e diária.");
    setSaving(true);
    try {
      await onSave({
        title: f.title, city: f.city, state: f.state, description: f.description,
        price_per_night: Number(f.price_per_night), deposit_percent: Number(f.deposit_percent),
        images: f.images, max_guests: Number(f.max_guests), bedrooms: Number(f.bedrooms), bathrooms: Number(f.bathrooms),
        amenities: f.amenities.split(",").map((s) => s.trim()).filter(Boolean),
        highlight: f.highlight || null, season_prices: cleanSeasons(f.season_prices),
      });
      onOpenChange(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white text-slate-900 border-[#EDE6DA] rounded-3xl max-w-2xl max-h-[92vh] overflow-y-auto" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="property-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl text-slate-900" style={{ fontFamily: "Fraunces, serif" }}>{initial ? "Editar imóvel" : "Novo imóvel"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className={labelCls}>Nome do imóvel</label>
            <input value={f.title} onChange={(e) => set({ title: e.target.value })} placeholder="Villa Sol — Pé na Areia" className={inputCls} data-testid="prop-title" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className={labelCls}>Cidade</label>
              <input value={f.city} onChange={(e) => set({ city: e.target.value })} className={inputCls} data-testid="prop-city" />
            </div>
            <div>
              <label className={labelCls}>UF</label>
              <input value={f.state} onChange={(e) => set({ state: e.target.value.toUpperCase().slice(0, 2) })} className={inputCls} data-testid="prop-state" />
            </div>
          </div>
          <div>
            <label className={labelCls}>Descrição</label>
            <textarea value={f.description} onChange={(e) => set({ description: e.target.value })} className={`${inputCls} h-24 py-2`} data-testid="prop-desc" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <Num label="Diária (R$)" value={f.price_per_night} onChange={(v) => set({ price_per_night: v })} testId="prop-price" />
            <Num label="Sinal (%)" value={f.deposit_percent} onChange={(v) => set({ deposit_percent: v })} testId="prop-deposit" />
            <Num label="Hóspedes" value={f.max_guests} onChange={(v) => set({ max_guests: v })} testId="prop-guests" />
            <Num label="Quartos" value={f.bedrooms} onChange={(v) => set({ bedrooms: v })} testId="prop-bedrooms" />
            <Num label="Banheiros" value={f.bathrooms} onChange={(v) => set({ bathrooms: v })} testId="prop-bathrooms" />
          </div>
          <PhotoManager images={f.images} onChange={(images) => set({ images })} />
          <SeasonPricesEditor seasons={f.season_prices} basePrice={f.price_per_night} onChange={(season_prices) => set({ season_prices })} />
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Comodidades (separe por vírgula)</label>
              <input value={f.amenities} onChange={(e) => set({ amenities: e.target.value })} placeholder="Piscina, Wi-Fi" className={inputCls} data-testid="prop-amenities" />
            </div>
            <div>
              <label className={labelCls}>Selo de destaque</label>
              <select value={f.highlight} onChange={(e) => set({ highlight: e.target.value })} className={inputCls} data-testid="prop-highlight">
                <option value="">Nenhum</option>
                <option value="Mais procurada">Mais procurada</option>
                <option value="Últimas datas!">Últimas datas!</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => onOpenChange(false)} className={btnGhost} data-testid="prop-cancel">Cancelar</button>
            <button onClick={submit} disabled={saving} className={btnPrimary} data-testid="prop-save">{saving ? "Salvando..." : "Salvar imóvel"}</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const PropertiesSection = ({ items, onAdd, onUpdate, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const save = (payload) => (editing ? onUpdate(editing.id, payload) : onAdd(payload));

  return (
    <Panel id="imoveis" title="Imóveis" subtitle="Fotos, preços, temporadas e o que aparece no site." testId="properties-section"
      action={<button onClick={() => { setEditing(null); setOpen(true); }} className={btnPrimary} data-testid="add-property-btn"><Plus size={15} /> Novo imóvel</button>}>
      <PropertyForm open={open} onOpenChange={setOpen} initial={editing} onSave={save} />
      {items.length === 0 ? (
        <p className="text-sm text-slate-500 text-center py-10">Nenhum imóvel ainda. Clique em "Novo imóvel".</p>
      ) : (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {items.map((p) => (
            <article key={p.id} className={`rounded-3xl border border-slate-200 overflow-hidden group hover:shadow-lg transition-shadow ${p.active ? "" : "opacity-60"}`} data-testid={`admin-prop-${p.id}`}>
              <div className="relative h-40 bg-slate-100">
                {p.images?.[0] ? <img src={mediaUrl(p.images[0])} alt={p.title} className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-slate-300"><ImageIcon size={32} /></div>}
                <span className="absolute bottom-2 left-2 text-[11px] font-bold bg-white/90 text-slate-700 px-2 py-0.5 rounded-full">{p.images?.length || 0} foto(s)</span>
                {p.season_prices?.length > 0 && (
                  <span className="absolute top-2 left-2 text-[11px] font-bold bg-[#FFCC00] text-blue-900 px-2 py-0.5 rounded-full flex items-center gap-1"><Sparkles size={11} /> {p.season_prices.length} temporada(s)</span>
                )}
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="font-bold text-slate-900 truncate">{p.title}</h3>
                    <p className="text-xs text-slate-500 flex items-center gap-1"><MapPin size={11} /> {p.city}, {p.state}</p>
                  </div>
                  <Switch className="data-[state=checked]:bg-[#0A66C2] data-[state=unchecked]:bg-slate-200 [&>span]:bg-white" checked={p.active} onCheckedChange={(v) => onUpdate(p.id, { active: v })} data-testid={`toggle-prop-${p.id}`} />
                </div>
                <div className="flex items-center justify-between mt-3">
                  <span className="font-extrabold text-[#0A66C2]">{BRL(p.price_per_night)}<span className="text-xs text-slate-400 font-medium">/noite</span></span>
                  <div className="flex">
                    <button className={`${iconBtn} hover:text-[#0A66C2]`} onClick={() => { setEditing(p); setOpen(true); }} data-testid={`edit-prop-${p.id}`}><Pencil size={15} /></button>
                    <button className={`${iconBtn} hover:text-red-600`} onClick={() => window.confirm(`Apagar "${p.title}"?`) && onDelete(p.id)} data-testid={`del-prop-${p.id}`}><Trash2 size={15} /></button>
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </Panel>
  );
};
